import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Api } from '../api/index.ts';
import type { Org } from '../api/OrgsApi.ts';
import type { Project, ProjectColor } from '../api/ProjectsApi.ts';

const LAST_ORG_KEY = 'dometry:lastOrg';

/**
 * The user's organizations, which one is current (from the URL), and that
 * org's projects. The URL is the source of truth for "current"; the last one is
 * remembered only to pick a sensible landing page.
 */
export const useWorkspaceStore = defineStore('workspace', () => {
  const orgs = ref<Org[]>([]);
  const currentSlug = ref<string | null>(null);
  const projectsByOrg = ref<Record<string, Project[]>>({});
  let api: Api | null = null;

  const current = computed(() => orgs.value.find((o) => o.slug === currentSlug.value) ?? null);
  const projects = computed(() =>
    currentSlug.value ? (projectsByOrg.value[currentSlug.value] ?? []) : [],
  );

  function bind(apiClients: Api): void {
    api = apiClients;
  }

  function client(): Api {
    if (!api) throw new Error('Workspace store used before bind(api)');
    return api;
  }

  async function loadOrgs(): Promise<void> {
    orgs.value = (await client().orgs.list()).orgs;
  }

  /**
   * Where "home" is: the last org visited, else the first team org, else the
   * sign-up one; never a paused one while another opens (else the paused page).
   */
  function landingSlug(): string | null {
    const open = orgs.value.filter((o) => !o.paused);
    const last = safeGet(LAST_ORG_KEY);
    if (last && open.some((o) => o.slug === last)) return last;
    return (open.find((o) => o.kind === 'team') ?? open[0] ?? orgs.value[0])?.slug ?? null;
  }

  /** Show an org as current without opening it (a paused one: its name, none of its projects). */
  function show(slug: string): void {
    currentSlug.value = slug;
  }

  async function select(slug: string): Promise<void> {
    currentSlug.value = slug;
    safeSet(LAST_ORG_KEY, slug);
    await loadProjects(slug);
  }

  async function loadProjects(slug: string): Promise<void> {
    projectsByOrg.value = {
      ...projectsByOrg.value,
      [slug]: (await client().projects.list(slug)).projects,
    };
  }

  async function createOrg(name: string): Promise<Org> {
    const { org } = await client().orgs.create(name);
    orgs.value = [...orgs.value, org];
    return org;
  }

  async function createProject(
    slug: string,
    input: { key: string; name: string; description?: string; color?: ProjectColor },
  ) {
    const { project } = await client().projects.create(slug, input);
    await loadProjects(slug);
    return project;
  }

  function reset(): void {
    orgs.value = [];
    currentSlug.value = null;
    projectsByOrg.value = {};
  }

  return {
    orgs,
    currentSlug,
    current,
    projects,
    bind,
    loadOrgs,
    landingSlug,
    show,
    select,
    loadProjects,
    createOrg,
    createProject,
    reset,
  };
});

function safeGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Storage blocked: only the landing-page memory is lost.
  }
}
